import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, MapPin, Clock, Plus, ArrowLeft, Ticket, 
  Users, ChevronRight, Edit2, Trash2, Loader2, 
  CalendarRange, Tag, DollarSign, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import AdminLayout from '../../components/AdminLayout';

const SubeventsManagerPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [parentEvent, setParentEvent] = useState(null);
  const [subevents, setSubevents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFullPassModal, setShowFullPassModal] = useState(false);
  const [fullPassPrice, setFullPassPrice] = useState('');
  const [fullPassQuantity, setFullPassQuantity] = useState('');
  const [saving, setSaving] = useState(false);
  
  const [newSubevent, setNewSubevent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    address: '',
    ticket_price: '',
    ticket_quantity: '',
    image_url: ''
  });

  useEffect(() => {
    loadData();
  }, [eventId]);

  const loadData = async () => {
    try {
      const [eventRes, subeventsRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getSubevents(eventId)
      ]);
      setParentEvent(eventRes.data);
      setSubevents(subeventsRes.data || []);
      setFullPassPrice(eventRes.data.full_pass_price || '');
      setFullPassQuantity(eventRes.data.full_pass_quantity || '');
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubevent = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Combine date and time
      const dateTime = new Date(`${newSubevent.date}T${newSubevent.time || '00:00'}`);
      
      const subeventData = {
        title: newSubevent.title,
        description: newSubevent.description,
        date: dateTime.toISOString(),
        location: newSubevent.location,
        address: newSubevent.address,
        ticket_price: parseFloat(newSubevent.ticket_price) || 0,
        ticket_quantity: parseInt(newSubevent.ticket_quantity) || 100,
        image_url: newSubevent.image_url || parentEvent?.image_url,
        artists: [],
        status: 'draft'
      };
      
      await eventsApi.createSubevent(eventId, subeventData);
      toast.success('Subevento creado correctamente');
      setShowCreateModal(false);
      setNewSubevent({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        address: '',
        ticket_price: '',
        ticket_quantity: '',
        image_url: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating subevent:', error);
      toast.error('Error al crear el subevento');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFullPass = async () => {
    if (!fullPassPrice || !fullPassQuantity) {
      toast.error('Completá el precio y cantidad del pase completo');
      return;
    }
    
    setSaving(true);
    try {
      await eventsApi.updateFullPass(eventId, parseFloat(fullPassPrice), parseInt(fullPassQuantity));
      toast.success('Pase completo actualizado');
      setShowFullPassModal(false);
      loadData();
    } catch (error) {
      console.error('Error updating full pass:', error);
      toast.error('Error al actualizar el pase completo');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-AR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate('/admin/eventos')}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white">{parentEvent?.title}</h1>
            <p className="text-zinc-400 text-sm">Gestionar subeventos</p>
          </div>
        </div>

        {/* Parent Event Info Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-6">
            {parentEvent?.image_url && (
              <img 
                src={parentEvent.image_url} 
                alt={parentEvent.title}
                className="w-32 h-32 object-cover rounded-lg"
              />
            )}
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-2">{parentEvent?.title}</h2>
              <p className="text-zinc-400 text-sm mb-4">{parentEvent?.description}</p>
              <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                <div className="flex items-center gap-2">
                  <CalendarRange size={16} className="text-[#D4AF37]" />
                  <span>{subevents.length} subeventos</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-[#D4AF37]" />
                  <span>{parentEvent?.location}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Pass Configuration */}
        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-orange-500/10 border border-[#D4AF37]/30 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Tag className="text-[#D4AF37]" size={20} />
                Pase Completo
              </h3>
              <p className="text-zinc-400 text-sm mt-1">
                Permite acceso a todos los subeventos con un solo ticket
              </p>
              {parentEvent?.full_pass_price && (
                <div className="flex items-center gap-4 mt-3">
                  <span className="text-[#D4AF37] font-bold text-xl">
                    ${parentEvent.full_pass_price.toLocaleString('es-AR')}
                  </span>
                  <span className="text-zinc-500 text-sm">
                    {parentEvent.full_pass_quantity - (parentEvent.full_passes_sold || 0)} disponibles
                  </span>
                </div>
              )}
            </div>
            <Button
              onClick={() => setShowFullPassModal(true)}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
            >
              <Edit2 size={16} className="mr-2" />
              Configurar
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-white">Subeventos ({subevents.length})</h3>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
          >
            <Plus size={16} className="mr-2" />
            Agregar Subevento
          </Button>
        </div>

        {/* Subevents List */}
        {subevents.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <Calendar size={48} className="mx-auto text-zinc-600 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Sin subeventos</h3>
            <p className="text-zinc-400 text-sm mb-4">
              Agregá subeventos para crear tu evento de varios días
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
            >
              <Plus size={16} className="mr-2" />
              Crear primer subevento
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {subevents.map((subevent, index) => (
              <motion.div
                key={subevent.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {/* Date Badge */}
                  <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg flex flex-col items-center justify-center">
                    <span className="text-[#D4AF37] text-xs uppercase">
                      {new Date(subevent.date).toLocaleDateString('es-AR', { weekday: 'short', timeZone: 'America/Argentina/Buenos_Aires' })}
                    </span>
                    <span className="text-white text-xl font-bold">
                      {new Date(subevent.date).getDate()}
                    </span>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-bold truncate">{subevent.title}</h4>
                    <div className="flex flex-wrap gap-3 text-sm text-zinc-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatTime(subevent.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {subevent.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Ticket size={14} />
                        ${subevent.ticket_price?.toLocaleString('es-AR')}
                      </span>
                    </div>
                  </div>
                  
                  {/* Status & Actions */}
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      subevent.status === 'published' 
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {subevent.status === 'published' ? 'Publicado' : 'Borrador'}
                    </span>
                    <Link 
                      to={`/admin/eventos/${subevent.id}/editar`}
                      className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                    >
                      <Edit2 size={16} className="text-zinc-400" />
                    </Link>
                    <Link 
                      to={`/admin/eventos/${subevent.id}/dashboard`}
                      className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                    >
                      <ChevronRight size={16} className="text-zinc-400" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create Subevent Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCreateModal(false)}
            >
              <motion.div
                className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl font-bold text-white mb-4">Nuevo Subevento</h3>
                <p className="text-zinc-400 text-sm mb-6">
                  Parte de: <span className="text-[#D4AF37]">{parentEvent?.title}</span>
                </p>
                
                <form onSubmit={handleCreateSubevent} className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Título *</label>
                    <input
                      type="text"
                      value={newSubevent.title}
                      onChange={(e) => setNewSubevent({...newSubevent, title: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="Ej: Día 1 - Charla con DJ"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Descripción</label>
                    <textarea
                      value={newSubevent.description}
                      onChange={(e) => setNewSubevent({...newSubevent, description: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      rows={3}
                      placeholder="Descripción del subevento"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Fecha *</label>
                      <input
                        type="date"
                        value={newSubevent.date}
                        onChange={(e) => setNewSubevent({...newSubevent, date: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Hora</label>
                      <input
                        type="time"
                        value={newSubevent.time}
                        onChange={(e) => setNewSubevent({...newSubevent, time: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Ubicación *</label>
                    <input
                      type="text"
                      value={newSubevent.location}
                      onChange={(e) => setNewSubevent({...newSubevent, location: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="Ej: Hipódromo de Palermo"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Dirección</label>
                    <input
                      type="text"
                      value={newSubevent.address}
                      onChange={(e) => setNewSubevent({...newSubevent, address: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="Dirección completa"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Precio entrada $</label>
                      <input
                        type="number"
                        value={newSubevent.ticket_price}
                        onChange={(e) => setNewSubevent({...newSubevent, ticket_price: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Cantidad tickets</label>
                      <input
                        type="number"
                        value={newSubevent.ticket_quantity}
                        onChange={(e) => setNewSubevent({...newSubevent, ticket_quantity: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                        placeholder="100"
                        min="1"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                      disabled={saving}
                    >
                      {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-2" />}
                      Crear Subevento
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Full Pass Modal */}
        <AnimatePresence>
          {showFullPassModal && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFullPassModal(false)}
            >
              <motion.div
                className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full p-6"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl font-bold text-white mb-2">Configurar Pase Completo</h3>
                <p className="text-zinc-400 text-sm mb-6">
                  El pase completo permite acceso a todos los {subevents.length} subeventos
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Precio del Pase Completo ($)</label>
                    <input
                      type="number"
                      value={fullPassPrice}
                      onChange={(e) => setFullPassPrice(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="Ej: 15000"
                      min="0"
                    />
                    {subevents.length > 0 && (
                      <p className="text-xs text-zinc-500 mt-1">
                        Suma de entradas individuales: ${subevents.reduce((sum, s) => sum + (s.ticket_price || 0), 0).toLocaleString('es-AR')}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Cantidad de Pases Disponibles</label>
                    <input
                      type="number"
                      value={fullPassQuantity}
                      onChange={(e) => setFullPassQuantity(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="Ej: 100"
                      min="1"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowFullPassModal(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveFullPass}
                      className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                      disabled={saving}
                    >
                      {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                      Guardar
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default SubeventsManagerPage;
